/**
 * <st-hire-booking> – weekly-priced hire (minimum 2 weeks, paid in advance every 4 weeks until one week's
 * notice; unused weeks are refunded on return).
 *
 * The hire item is added with one billing cycle of quantity (weeks x units per week), and with the 4-weekly
 * selling plan (Shopify Subscriptions) when the product has one. The "Due today" total follows the chosen
 * delivery option. When "Delivery & collection" is chosen the delivery postcode is checked against the service
 * area (data-postcodes, e.g. "2000-2234,2500-2530"), and the one-off delivery fee item is added too.
 * Delivery method, postcode, start date and the agreed terms travel with the order as line item properties.
 * After adding, the page reloads with the cart drawer open (there is no separate cart page).
 * When the product has several variants (for example a mattress upgrade) they are radio buttons named "variant";
 * the chosen one sets the hire item, the weekly price, the total and the gallery photo.
 * In enquiry mode (data-mode="enquiry") the button opens the Talk to an expert popup with the choices filled in.
 */
class StHireBooking extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.start = this.form.querySelector('[data-ref="start"]');
    this.error = this.querySelector('[data-ref="error"]');
    this.submit = this.form.querySelector('[data-ref="submit"]');
    this.postcodeField = this.form.querySelector('[data-ref="postcodeField"]');
    this.postcode = this.form.querySelector('[data-ref="postcode"]');
    this.postcodeError = this.form.querySelector('[data-ref="postcodeError"]');
    this.ranges = String(this.dataset.postcodes || '')
      .split(',')
      .map((part) => part.trim().split('-').map(Number))
      .filter((range) => range.length && !Number.isNaN(range[0]))
      .map(([from, to]) => [from, Number.isNaN(to) || to === undefined ? from : to]);

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const pad = (n) => String(n).padStart(2, '0');
    const min = `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}`;
    this.start.min = min;
    if (!this.start.value) this.start.value = min;

    this.form.addEventListener('change', (event) => {
      if (event.target.name === 'variant') this.#selectVariant(event.target);
      this.#sync();
    });
    this.postcode?.addEventListener('input', () => {
      if (this.postcodeError) this.postcodeError.hidden = true;
    });
    this.form.addEventListener('submit', (event) => this.#onSubmit(event));
    this.#sync();
  }

  #selectVariant(input) {
    this.dataset.variantId = input.value;
    this.dataset.price = input.dataset.price;
    const weekPrice = this.closest('.st-hire')?.querySelector('[data-ref="weekPrice"]');
    if (weekPrice) weekPrice.textContent = this.#money((Number(input.dataset.price) || 0) * (Number(this.dataset.daysPerWeek) || 1));

    const gallery = document.querySelector('media-gallery');
    const slides = gallery ? Array.from(gallery.querySelectorAll('slideshow-slide')) : [];
    const index = input.dataset.mediaId
      ? slides.findIndex((slide) => slide.querySelector(`[data-media-id="${input.dataset.mediaId}"]`))
      : -1;
    if (index >= 0 && gallery.slideshow) gallery.slideshow.select(index, undefined, { animate: false });
  }

  #sync() {
    const delivery = this.form.querySelector('input[name="delivery"]:checked')?.value;
    const feeNote = this.querySelector('[data-ref="feeNote"]');
    const delivered = delivery === 'delivery' || delivery === 'upstairs';
    if (feeNote) feeNote.hidden = !delivered;
    this.#updateTotal(delivery);
    if (this.postcodeField) {
      this.postcodeField.hidden = !delivered;
      this.postcode.required = delivered;
      if (!delivered && this.postcodeError) this.postcodeError.hidden = true;
    }
  }

  // Prices in the data attributes are in cents. "Due today" = one billing cycle + the chosen delivery fee.
  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'AUD';
    const amount = cents / 100;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  }

  #fee(delivery) {
    if (delivery === 'delivery') return Number(this.dataset.feePrice) || 0;
    if (delivery === 'upstairs') return Number(this.dataset.feeUpstairsPrice) || 0;
    return 0;
  }

  #updateTotal(delivery) {
    const amount = this.querySelector('[data-ref="totalAmount"]');
    const note = this.querySelector('[data-ref="totalNote"]');
    if (!amount) return;
    const units = (Number(this.dataset.daysPerWeek) || 1) * (Number(this.dataset.weeks) || 4);
    const fee = this.#fee(delivery);
    amount.textContent = this.#money((Number(this.dataset.price) || 0) * units + fee);
    if (note) note.textContent = fee ? this.dataset.totalNoteDelivery : this.dataset.totalNote;
  }

  #inServiceArea(code) {
    if (!this.ranges.length) return true;
    const value = Number(code);
    return this.ranges.some(([from, to]) => value >= from && value <= to);
  }

  async #onSubmit(event) {
    event.preventDefault();
    if (!this.form.reportValidity()) return;

    const data = new FormData(this.form);
    const delivery = data.get('delivery');
    const postcode = String(data.get('postcode') || '').trim();

    // The postcode question is optional (hidden for now); only check the area when it is shown.
    if (this.postcode && delivery !== 'collect' && !this.#inServiceArea(postcode)) {
      if (this.postcodeError) this.postcodeError.hidden = false;
      this.postcode.focus();
      return;
    }

    const [y, m, d] = String(data.get('start')).split('-');

    // Enquiry-only items: hand the choices to the Talk to an expert popup instead of the cart.
    if (this.dataset.mode === 'enquiry') {
      const trigger = this.querySelector('[data-ref="enquiryTrigger"]');
      const chosen = this.form.querySelector('input[name="variant"]:checked');
      const deliveryText = this.form.querySelector('input[name="delivery"]:checked')?.closest('label')?.querySelector('strong')?.textContent?.trim();
      const fee = this.#fee(delivery);
      const parts = [];
      if (chosen) parts.push(chosen.closest('label')?.querySelector('strong')?.textContent?.trim());
      if (deliveryText) parts.push(fee ? `${deliveryText} (${this.#money(fee)})` : deliveryText);
      parts.push(`start ${d}-${m}-${y}`);
      if (delivery !== 'collect' && postcode) parts.push(`postcode ${postcode}`);
      if (trigger) {
        trigger.dataset.hireDetails = parts.filter(Boolean).join(', ');
        trigger.dataset.variantId = chosen?.value || this.dataset.variantId || '';
        trigger.dataset.variantTitle = chosen?.closest('label')?.querySelector('strong')?.textContent?.trim() || '';
        trigger.click();
      }
      return;
    }
    const hasPlan = Boolean(this.dataset.sellingPlan);
    const perCycle = (Number(this.dataset.daysPerWeek) || 1) * (Number(this.dataset.weeks) || 4);

    const delivered = delivery === 'delivery' || delivery === 'upstairs';
    const deliveryNames = {
      collect: this.dataset.collectLabel || 'Click & Collect',
      delivery: `${this.dataset.deliveryLabel} (${this.#money(this.#fee('delivery'))})`,
      upstairs: `${this.dataset.upstairsLabel} (${this.#money(this.#fee('upstairs'))})`,
    };

    const properties = {
      Type: 'Hire (weekly)',
      'Preferred start date': `${d}-${m}-${y}`,
      'Terms accepted': 'Hire Terms & Conditions',
      'Minimum term': '2 weeks',
      Billing: `Every ${this.dataset.weeks || 4} weeks, in advance`,
      'Delivery method': deliveryNames[delivery] || deliveryNames.collect,
    };
    if (delivered && postcode) properties['Delivery postcode'] = postcode;

    const hireItem = {
      id: Number(this.dataset.variantId),
      quantity: perCycle,
      properties,
    };
    if (hasPlan) hireItem.selling_plan = Number(this.dataset.sellingPlan);

    const items = [hireItem];

    const feeVariantId = delivery === 'upstairs' ? this.dataset.feeUpstairsVariantId : this.dataset.feeVariantId;
    if (delivered && feeVariantId) {
      items.push({
        id: Number(feeVariantId),
        quantity: 1,
        properties: {
          'For hire item': this.dataset.title,
          'Preferred start date': properties['Preferred start date'],
        },
      });
    }

    this.submit.disabled = true;
    this.error.hidden = true;

    try {
      const root = window.Shopify?.routes?.root || '/';

      // One hire booking per cart: a new booking replaces any earlier hire and delivery fee lines.
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const updates = {};
      for (const line of cart.items || []) {
        if (line.properties && (line.properties.Type === 'Hire (weekly)' || line.properties['For hire item'])) {
          updates[line.key] = 0;
        }
      }
      if (Object.keys(updates).length) {
        await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('add failed');
      window.location.href = `${window.location.pathname}?cart=open`;
    } catch (error) {
      this.error.hidden = false;
      this.submit.disabled = false;
    }
  }
}

if (!customElements.get('st-hire-booking')) customElements.define('st-hire-booking', StHireBooking);